import { lazy, Suspense } from "react";
import { Layout } from "../../components/Layout";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useLang } from "../../contexts/LangContext";
import { useAuth } from "../../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import {
  ShieldCheck, Users, FileClock, CreditCard, MessageSquare,
  LayoutDashboard, Settings,
} from "lucide-react";

// Lazy-load tabs. React.lazy requires a default export, so for our named
// exports we map .Tab to { default }. Each tab becomes its own chunk so the
// initial /admin bundle only ships the overview tab — operators that just
// open /admin to glance at metrics download ~5× less JS.
const OverviewTab = lazy(() => import("./OverviewTab").then((m) => ({ default: m.OverviewTab })));
const UsersTab    = lazy(() => import("./UsersTab").then((m) => ({ default: m.UsersTab })));
const BillingTab  = lazy(() => import("./BillingTab").then((m) => ({ default: m.BillingTab })));
const SupportTab  = lazy(() => import("./SupportTab").then((m) => ({ default: m.SupportTab })));
const ConfigTab   = lazy(() => import("./ConfigTab").then((m) => ({ default: m.ConfigTab })));
const AuditTab    = lazy(() => import("./AuditTab").then((m) => ({ default: m.AuditTab })));

function TabFallback() {
  return (
    <Card className="p-8 text-center text-slate-400" data-testid="admin-tab-loading">
      …
    </Card>
  );
}

/** Admin-only command centre. Sidebar entry is gated; this page also
 *  hard-checks `user.is_admin` from the auth context and redirects
 *  non-admins to /dashboard.
 *
 *  Refactor note (Iter-117): the original 1170-line monolith was split
 *  per-tab into AdminShared/OverviewTab/UsersTab/ConfigTab/AuditTab.
 *  This file is now just routing + auth gate + tab list. */
export default function AdminPanel() {
  const { user } = useAuth();
  const { lang } = useLang();

  if (user === null) return null; // still loading
  if (user === false || !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  // PT-BR users see Spanish UI for now (ES↔PT lexical overlap ~89%);
  // per-string PT-BR translations land in a follow-up iteration.
  const isEs = lang !== "en";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6 fade-up" data-testid="admin-panel">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="h-12 w-12 rounded-2xl bg-sky-100 text-sky-700 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
              {isEs ? "Panel de administración" : "Admin panel"}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {isEs
                ? "Overview ejecutivo, gestión de usuarios, billing, comunicaciones, configuración e historial de cambios."
                : "Executive overview, user management, billing, communications, configuration and change history."}
            </p>
          </div>
          <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 gap-1">
            <ShieldCheck className="h-3 w-3" />
            {user?.email}
          </Badge>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
            <TabsList className="grid w-max sm:w-full grid-cols-6 min-w-full">
              <TabsTrigger value="overview" data-testid="admin-tab-overview" className="text-xs sm:text-sm">
                <LayoutDashboard className="h-4 w-4 mr-1.5 shrink-0" />
                {isEs ? "Overview" : "Overview"}
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users" className="text-xs sm:text-sm">
                <Users className="h-4 w-4 mr-1.5 shrink-0" />
                {isEs ? "Usuarios" : "Users"}
              </TabsTrigger>
              <TabsTrigger value="billing" data-testid="admin-tab-billing" className="text-xs sm:text-sm">
                <CreditCard className="h-4 w-4 mr-1.5 shrink-0" />
                {isEs ? "Billing" : "Billing"}
              </TabsTrigger>
              <TabsTrigger value="support" data-testid="admin-tab-support" className="text-xs sm:text-sm">
                <MessageSquare className="h-4 w-4 mr-1.5 shrink-0" />
                {isEs ? "Comunicaciones" : "Comms"}
              </TabsTrigger>
              <TabsTrigger value="config" data-testid="admin-tab-config" className="text-xs sm:text-sm">
                <Settings className="h-4 w-4 mr-1.5 shrink-0" />
                {isEs ? "Configuración" : "Config"}
              </TabsTrigger>
              <TabsTrigger value="audit" data-testid="admin-tab-audit" className="text-xs sm:text-sm">
                <FileClock className="h-4 w-4 mr-1.5 shrink-0" />
                {isEs ? "Auditoría" : "Audit"}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview" className="mt-4">
            <Suspense fallback={<TabFallback />}>
              <OverviewTab isEs={isEs} />
            </Suspense>
          </TabsContent>
          <TabsContent value="users" className="mt-4">
            <Suspense fallback={<TabFallback />}>
              <UsersTab isEs={isEs} adminId={user.id} />
            </Suspense>
          </TabsContent>
          <TabsContent value="billing" className="mt-4">
            <Suspense fallback={<TabFallback />}>
              <BillingTab isEs={isEs} />
            </Suspense>
          </TabsContent>
          <TabsContent value="support" className="mt-4">
            <Suspense fallback={<TabFallback />}>
              <SupportTab isEs={isEs} />
            </Suspense>
          </TabsContent>
          <TabsContent value="config" className="mt-4">
            <Suspense fallback={<TabFallback />}>
              <ConfigTab isEs={isEs} />
            </Suspense>
          </TabsContent>
          <TabsContent value="audit" className="mt-4">
            <Suspense fallback={<TabFallback />}>
              <AuditTab isEs={isEs} />
            </Suspense>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
